import React from "react";
import { makeStyles } from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import { TimeReportRowSource } from "@bonliva-crm/shared";
import WithTooltip from "@components/WithTooltip";
import {
  BemloMark,
  CrmMark,
  InkopioMark,
  ManualMark,
  MedloMark,
  MobileAppMark,
  PaperMark,
  UnknownSourceMark
} from "./brand-icons";

type MarkComponent = React.ComponentType<{ className?: string; title?: string }>;

/**
 * How each reporting channel is drawn. Keyed by `TimeReportRowSource`, the
 * vocabulary shared verbatim with ERP (ADR-0008), so a channel added there
 * shows up here as soon as the enum moves — and until it does, `Unknown`'s
 * outline is what an unrecognised value falls back to rather than a blank cell.
 *
 * Provider names are proper nouns and stay untranslated; the rest read from the
 * locale, because "typed in the CRM" is a description rather than a brand.
 */
const MARKS: Record<TimeReportRowSource, { label: string; Mark: MarkComponent }> = {
  [TimeReportRowSource.Bemlo]: { label: "Bemlo", Mark: BemloMark },
  [TimeReportRowSource.Medlo]: { label: "Medlo", Mark: MedloMark },
  [TimeReportRowSource.Inkopio]: { label: "Inköpio", Mark: InkopioMark },
  [TimeReportRowSource.Paper]: {
    label: locale.timereportRowSource.Paper,
    Mark: PaperMark
  },
  [TimeReportRowSource.MobileApp]: {
    label: locale.timereportRowSource.MobileApp,
    Mark: MobileAppMark
  },
  [TimeReportRowSource.Crm]: { label: locale.timereportRowSource.Crm, Mark: CrmMark },
  [TimeReportRowSource.Manual]: {
    label: locale.timereportRowSource.Manual,
    Mark: ManualMark
  },
  [TimeReportRowSource.Unknown]: {
    label: locale.timereportRowSource.Unknown,
    Mark: UnknownSourceMark
  }
};

type Props = {
  /** The stored channel. Anything unrecognised — including null — draws the "not recorded" mark. */
  source?: string | null;
  /** The channel's own id for this row, shown in the tooltip so a number can be traced. */
  sourceId?: string | null;
  /** Deep link back into the channel. Makes the mark a link when the channel has an addressable page. */
  sourceUrl?: string | null;
};

/**
 * Where one row of hours came from, drawn beside it (ADR-0008).
 *
 * A CRM user checking a number against its origin asks two things: which
 * channel reported it, and can I open that. The mark answers the first at a
 * glance and the link answers the second without leaving the row — which is the
 * point of putting it here rather than in a detail panel a click away.
 *
 * Renders the "not recorded" mark rather than nothing when a row has no
 * channel: a blank cell beside a row that does have one reads as a rendering
 * bug, and rows written before provenance existed are a real, shrinking
 * population that people will ask about.
 */
export function TimereportRowSourceMark(props: Props) {
  const classes = useStyles({});
  const known = MARKS[props.source as TimeReportRowSource];
  const { label, Mark } = known ?? MARKS[TimeReportRowSource.Unknown];

  const title = [
    label,
    // The id is what someone searches for in the origin system, so it belongs
    // in the tooltip even when there is no link to follow.
    props.sourceId ? `· ${props.sourceId}` : null,
    props.sourceUrl ? `· ${locale.timereportRowSource.OpenOrigin}` : null
  ]
    .filter(Boolean)
    .join(" ");

  const mark = <Mark className={classes.mark} title={label} />;

  return (
    <WithTooltip title={title}>
      {props.sourceUrl ? (
        <a
          href={props.sourceUrl}
          target="_blank"
          rel="noreferrer noopener"
          className={classes.link}
          // The row is clickable in some tables; opening the origin must not
          // also open the row's editor.
          onClick={(e) => e.stopPropagation()}
        >
          {mark}
        </a>
      ) : (
        <span className={classes.link}>{mark}</span>
      )}
    </WithTooltip>
  );
}

const useStyles = makeStyles(() => ({
  link: {
    display: "inline-flex",
    alignItems: "center",
    lineHeight: 0
  },
  mark: {
    width: 16,
    height: 16,
    flex: "0 0 auto"
  }
}));
